import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation } from '@apollo/client';
import { Heading, Body } from '../modules/Typography';
import Paper from '../modules/Paper';
import { Col } from '../modules/Grid';
import people from '../../assets/people.png';
import TextField from '../modules/TextField';
import TextArea from '../modules/TextArea';
import Button from '../modules/Button';
import { REQUEST_INFO } from '../../queries';
import DOMPurify from 'dompurify';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

// --- Define your reCAPTCHA keys here ---
const RECAPTCHA_KEYS = {
    DEVELOPMENT:'6LflUHArAAAAABuVbNXUO2SxA9_88fZpE98lDHSY',
    STAGING:    '6LeHgXUrAAAAANwTdEs7UyJnuE2OFKGXWTiCk0-1',
    PRODUCTION: '6LfgYHsrAAAAAIT5AnY1E7YemUTNC8gbYhTAGRqG',
    DEFAULT:    '6LfgYHsrAAAAAIT5AnY1E7YemUTNC8gbYhTAGRqG'
};

const getRecaptchaKeyByEnvironment = () => {
    const hostname = window.location.hostname;
    let environmentKey = 'UNKNOWN'; // Default, if no match

    const prefix = hostname.substring(0, 3).toLowerCase();

    switch (prefix) {
        case 'dev':
            environmentKey = 'DEVELOPMENT';
            break;
        case 'stg':
            environmentKey = 'STAGING';
            break;
        case 'med':
            environmentKey = 'PRODUCTION';
            break;
        default:
            if (hostname.includes('localhost')) {
                environmentKey = 'DEVELOPMENT';
            }
            break;
    }

    return {
        key: RECAPTCHA_KEYS[environmentKey],
        environmentTag: environmentKey
    };
};
// ---------------------------------------------------------------------

const Contact = () => {
    const [sendRequest] = useMutation(REQUEST_INFO);
    const [formOpen, setFormOpen] = useState(true);
    const [message, setMessage] = useState('');
    const [subject, setSubject] = useState('');
    const [email, setEmail] = useState('');

    const [errors, setErrors] = useState([]); //SIP-541// Now handles email and message errors
    const MAX_MESSAGE_LENGTH = 500; //SIP-541// Defines the maximum character limit for the message
    const [formSubmitted, setFormSubmitted] = useState(false); //SIP-541B// New state to control if the form has been attempted to submit
    const [recaptchaToken, setRecaptchaToken] = useState(''); //SIP-541E: State to store reCAPTCHA token
    const [recaptchaError, setRecaptchaError] = useState(false); //SIP-541E: State for reCAPTCHA error
    // SIP-541F: Define ENVIRONMENT_TAG based on the Node environment (development/production)
    const {environmentTag: ENVIRONMENT_TAG } = getRecaptchaKeyByEnvironment();

    // SIP-541E: Hook to execute reCAPTCHA
    const { executeRecaptcha } = useGoogleReCaptcha();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // SIP-541B// Handler for changes in the subject field
    const handleSubjectChange = (e) => {
        const { value } = e.target;
        setSubject(value);
        // SIP-541B// If the form has been attempted to submit and the field is not empty, remove the 'subject' error
        if (formSubmitted && value.trim() !== '' && errors.includes('subject')) {
            setErrors(prevErrors => prevErrors.filter(err => err !== 'subject'));
        }
    };

    // SIP-541// Handler for changes in the message field, also validates length
    // SIP-541G: Improved on-keypress validation for message length.
    const handleMessageChange = (e) => {
        const { value } = e.target;
        setMessage(value); // Updates the message state

        // Determine if the message has a length issue or is empty
        const isMessageEmpty = value.trim() === '';
        const isMessageTooLong = value.length > MAX_MESSAGE_LENGTH;

        // Create a temporary set of errors for the message field
        let newErrorsForMessage = [];
        if (isMessageEmpty) {
            newErrorsForMessage.push('message'); // Add error if empty
        } else if (isMessageTooLong) {
            newErrorsForMessage.push('message'); // Add error if too long
        }

        // Update the overall errors state based on the message field's new status
        setErrors(prevErrors => {
            // Filter out existing 'message' errors
            const filteredErrors = prevErrors.filter(err => err !== 'message');
            // Add back 'message' error if it should be there
            if (newErrorsForMessage.includes('message')) {
                return [...filteredErrors, 'message'];
            }
            return filteredErrors;
        });
    };

    // SIP-541B// Centralized form validation function
    // SIP-541G: Now accepts the reCAPTCHA token directly for immediate validation.
    const validateForm = (tokenToCheck) => {
        const newErrors = [];

        // Validation of Subject: Required
        if (subject.trim() === '') {
            newErrors.push('subject'); //SIP-541B
        }

        // Validation of Message: Required and length
        if (message.trim() === '') {
            newErrors.push('message'); //SIP-541B
        } else if (message.length > MAX_MESSAGE_LENGTH) {
            newErrors.push('message'); //SIP-541B
        }

        // Email Validation: Required and format (relying on runEmailRegex to update errors state)
        // Note: runEmailRegex directly modifies the `errors` state.
        // For synchronous validation within validateForm, we check `email.trim() === ''`
        // and then let runEmailRegex handle the format specific error.
        if (email.trim() === '') { //SIP-541D: Check if email is empty for required validation
            newErrors.push('email');
        } else if (!runEmailRegex(email)) { //SIP-541D: Call runEmailRegex for format validation
            // If runEmailRegex returns false, it already added 'email' to errors
            // No need to explicitly push 'email' here again if runEmailRegex already does it
            if (!newErrors.includes('email')) { // Ensure no duplicate
                newErrors.push('email');
            }
        }

        // SIP-541E: reCAPTCHA validation
        // SIP-541G: Uses tokenToCheck for reCAPTCHA validation
        if (formSubmitted && !tokenToCheck) { // Only set error if form has been attempted to submit
            setRecaptchaError(true);
            // newErrors.push('recaptcha'); // Could add 'recaptcha' to newErrors if you want a general form error
        } else {
            setRecaptchaError(false);
        }

        setErrors(newErrors); // Updates the errors state
        // SIP-541E: Form is valid only if no field errors AND reCAPTCHA token exists
        // SIP-541G: Validity depends on newErrors and tokenToCheck
        return newErrors.length === 0 && tokenToCheck;

    };

    const handleSend = async (e) => {
        e.preventDefault();
        setFormSubmitted(true); //SIP-541B// Marks that the form submission was attempted

        // SIP-541G: Ensure reCAPTCHA token is available before validation.
        let token = recaptchaToken; // Try to use the current token from state first
        if (!token && executeRecaptcha) { // If no token, generate it
            try {
                token = await executeRecaptcha('contact_form_submit'); // 'contact_form_submit' is an action name
                setRecaptchaToken(token); // Store the token in state for future re-renders
            } catch (error) {
                console.error('reCAPTCHA execution failed:', error);
                setRecaptchaError(true);
                // If execution fails, 'token' will remain falsy, which will be handled by validateForm
            }
        } else if (!executeRecaptcha) {
            console.error('reCAPTCHA not loaded.');
            setRecaptchaError(true);
        }

        // SIP-541G: Execute all validations by passing the token directly.
        const isValid = validateForm(token); // Pass the token directly to validateForm

        // SIP-541B// If the form is not valid, stop submission
        if (!isValid) {
            console.log('Invalid form. Please review the required fields, their correct format, and complete reCAPTCHA if required.'); //SIP-541E
            return;
        }

        // SIP-541// Apply trim() to remove leading/trailing whitespace
        // SIP-541// Apply DOMPurify.sanitize() to prevent XSS attacks in subject and message
        const sanitizedSubject = DOMPurify.sanitize(subject.trim());
        const sanitizedMessage = DOMPurify.sanitize(message.trim());
        const sanitizedEmail = email.trim(); // Email only needs trim

        const variables = {
            subject: sanitizedSubject,
            message: sanitizedMessage, // SIP-541F
            recaptchaToken: token, // SIP-541E: Include reCAPTCHA token in variables // SIP-541G: Use the generated/validated token
            email: sanitizedEmail, // Ensure email is in the correct place.
        };

        try {
            await sendRequest({
                variables
            });
            setFormOpen(false); // Show success message
            // SIP-541B// Clear the form and reset validation states after successful submission
            setEmail('');
            setSubject('');
            setMessage('');
            setErrors([]);
            setFormSubmitted(false);
            setRecaptchaToken(''); // SIP-541E: Clear reCAPTCHA token
            setRecaptchaError(false); // SIP-541E: Clear reCAPTCHA error
        } catch (error) {
            console.error('Error submitting form:', error); //SIP-541B
            //alert('An error occurred while sending your message. Please try again later.'); //SIP-541B
        }
    };

    const testEmail = (e) => {
        const { value } = e.target;
        setEmail(value); //SIP-541B: Updates the email state
        runEmailRegex(value); // Validates email format
    };

    function runEmailRegex(value) {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if (re.test(value)) {
            // SIP-541: Remove 'email' from errors if valid
            setErrors(prevErrors => prevErrors.filter(error => error !== 'email'));
            return true;
        } else {
            // SIP-541: Add 'email' to errors if invalid
            setErrors(prevErrors => {
                if (!prevErrors.includes('email')) {
                    return [...prevErrors, 'email'];
                }
                return prevErrors;
            });
            return false;
        }
    }

    return (
        <>
            <Helmet><title>Disney Media Tech Specs | Contact</title></Helmet>
            <div className='Document'>
                <div className='Document__header'>
                    <Heading number={5} heavy block className='Document__title'>Questions</Heading>
                </div>
                <Paper className='Contact__paper' elevation={6}>
                    <Col className='Contact__wrapper'>
                        <Heading number={5} heavy className='Contact__title'>Contact Us</Heading>
                        <div>
                            <Body className=''>{formOpen ? 'For questions regarding specifications or site support.' : 'Thanks for contacting us, one of us will be in touch with you shortly.'} </Body>
                        </div>

                        <Col className='Contact__form'>
                            {formOpen &&
                                <form className='Contact__form__wrapper' onSubmit={handleSend}> {/* SIP-541B: Moves onSubmit to the form */}
                                    <TextField
                                        className='Contact__form__input'
                                        placeholder='Subject' //SIP-541F                                        
                                        value={subject} // SIP-541B: Controls the value with state
                                        onChange={handleSubjectChange} // SIP-541B: Uses the new handler
                                        error={formSubmitted && errors.includes('subject')} // SIP-541B: Show error only after submit attempt
                                        errorMessage={formSubmitted && errors.includes('subject') ? 'Subject is required.' : ''} // SIP-541B: Message if empty
                                        required // SIP-541B: HTML attribute for UX
                                    />
                                    <TextField
                                        id='emailField'
                                        className='Contact__form__input'
                                        placeholder='Your Email'
                                        value={email}
                                        onBlur={testEmail} // Email format validation continues on onBlur
                                        onChange={testEmail} // SIP-541B: Calls testEmail to validate on each type
                                        error={formSubmitted && errors.includes('email')} // SIP-541B: Show error only after submit attempt
                                        errorMessage={formSubmitted && errors.includes('email') ? 'Please enter a valid email' : ''} // SIP-541D: Simplified error message for email
                                        required // SIP-541B: HTML attribute for UX
                                    />
                                    <TextArea
                                        placeholder='Message: Max. 500 Characters'
                                        contact
                                        id='messageField' //SIP-541
                                        required //SIP-541B: HTML attribute for UX
                                        value={message} //SIP-541 Controls TextArea value with 'message' state
                                        maxLength={MAX_MESSAGE_LENGTH} //SIP-541// Adds HTML maxLength attribute for UX
                                        onChange={handleMessageChange} //SIP-541 Uses the new handler for the message
                                        error={formSubmitted && errors.includes('message')} //SIP-541B: Show error only after submit attempt
                                        errorMessage={
                                            formSubmitted && errors.includes('message') //SIP-541C: Check error type
                                                ? (message.trim() === '' //SIP-541C: If empty
                                                    ? 'Message is required.'
                                                    : `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters. Current: ${message.length}`) //SIP-541C: If length is exceeded
                                                : `Current: ${message.length} / ${MAX_MESSAGE_LENGTH}` //SIP-541C: Show character counter by default
                                        }
                                    />
                                    {/* SIP-541E: Display reCAPTCHA error message */}
                                    {formSubmitted && recaptchaError && (
                                        <div style={{ color: 'red', marginTop: '10px', fontSize: '0.8em' }}>
                                            reCAPTCHA verification failed. Please try again.
                                        </div>
                                    )}
                                    <Button
                                        type='primary'
                                        color='primary-light'
                                        className='Contact__form__button'
                                        // onClick={(e) => handleSend(e)} // SIP-541B: Removed, now the form handles submission
                                    >Submit
                                    </Button>
                                </form>}
                            <Col className='Contact__image'>
                                <img src={people} alt="People" /> {/* SIP-541B: Added alt attribute for accessibility */}
                            </Col>

                        </Col>

                    </Col>
                </Paper>

            </div>

        </>
    );
};
// SIP-541E: Wrap the Contact component with GoogleReCaptchaProvider
const ContactWithRecaptcha = () => {
    const { key: recaptchaKey } = getRecaptchaKeyByEnvironment(); // Get the key here

    return (
        <GoogleReCaptchaProvider reCaptchaKey={recaptchaKey}>
            <Contact />
        </GoogleReCaptchaProvider>
    );
};

export default ContactWithRecaptcha; // SIP-541E: Export the wrapped component
//export default Contact;